import { lazy, Suspense, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MobileStickyCTA } from "@/components/MobileStickyCTA";
import { WhatsAppFAB } from "@/components/WhatsAppFAB";
import { PageSkeleton, ServicesSkeleton } from "@/components/Skeleton";

// Route-based code splitting — each page ships in its own chunk.
const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Courses = lazy(() => import("@/pages/Services"));
const Testimonials = lazy(() => import("@/pages/Testimonials"));
const Blog = lazy(() => import("@/pages/Blog"));
const Resources = lazy(() => import("@/pages/Blog"));
const Media = lazy(() => import("@/pages/Media"));
const Contact = lazy(() => import("@/pages/Contact"));

const RouteFocusManager = ({ mainRef }) => {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    // Skip focus shift on the very first mount so it doesn't override deep-link scroll.
    if (first.current) {
      first.current = false;
      return;
    }
    // Announce route change: move focus to <main> for screen readers / keyboard users.
    if (mainRef.current) {
      mainRef.current.focus({ preventScroll: true });
    }
  }, [pathname, mainRef]);
  return null;
};

const SmoothScroll = () => {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
};

// Pick the right skeleton per route so the fallback matches actual layout.
const SuspenseByRoute = ({ children }) => {
  const { pathname } = useLocation();
  const Fallback = pathname === "/courses" || pathname === "/services" ? <ServicesSkeleton /> : <PageSkeleton />;
  return <Suspense fallback={Fallback}>{children}</Suspense>;
};

function App() {
  const mainRef = useRef(null);
  return (
    <HelmetProvider>
      <div className="App relative">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-sm focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:text-primary-foreground"
        >
          Skip to content
        </a>
        <div className="grain-overlay" aria-hidden="true" />
        <BrowserRouter>
          <SmoothScroll />
          <RouteFocusManager mainRef={mainRef} />
          <Navbar />
          <main
            id="main-content"
            ref={mainRef}
            tabIndex={-1}
            className="outline-none"
          >
            <SuspenseByRoute>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/services" element={<Navigate to="/courses" replace />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/media" element={<Media />} />
                <Route path="/contact" element={<Contact />} />
              </Routes>
            </SuspenseByRoute>
          </main>
          <Footer />
          <MobileStickyCTA />
          <WhatsAppFAB />
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
      </div>
    </HelmetProvider>
  );
}

export default App;
